import type { ReactNode } from 'react';
import { Spinner } from './DropZone';

interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}

/** Calm, informative empty state used before any work has been done. */
export function EmptyState({ title, description, icon, action }: EmptyStateProps) {
  return (
    <div className="flex h-full min-h-56 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-line-strong bg-surface/70 p-8 text-center">
      {icon ? (
        <span
          aria-hidden="true"
          className="flex h-12 w-12 items-center justify-center rounded-pill bg-accent-soft text-accent-strong [&>svg]:h-6 [&>svg]:w-6"
        >
          {icon}
        </span>
      ) : null}
      <h2 className="text-lg font-semibold tracking-tight text-ink">{title}</h2>
      {description ? (
        <p className="max-w-prose text-sm text-ink-soft">{description}</p>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/** Loading state for a pending local computation. */
export function LoadingState({ label }: { label: string }) {
  return (
    <div
      className="flex h-full min-h-56 flex-col items-center justify-center gap-3 rounded-lg border border-line bg-surface/80 p-8 text-center"
      role="status"
    >
      <Spinner className="h-6 w-6 border-[3px]" />
      <p className="text-sm font-medium text-ink-soft">{label}</p>
    </div>
  );
}

/** Error state with an accessible alert role and a friendly heading. */
export function ErrorState({ message, title = 'Something went wrong' }: { message: ReactNode; title?: string }) {
  return (
    <div
      className="flex h-full min-h-56 flex-col items-center justify-center gap-2 rounded-lg border border-danger/40 bg-danger-soft p-8 text-center"
      role="alert"
    >
      <h2 className="text-lg font-semibold text-danger">{title}</h2>
      <p className="max-w-prose text-sm text-ink">{message}</p>
    </div>
  );
}

/** Small inline success/notice strip for transient confirmations. */
export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  children: ReactNode;
}) {
  const tones = {
    info: 'border-accent/20 bg-accent-soft text-navy',
    success: 'border-verified/25 bg-verified-soft text-verified',
    warning: 'border-gap/25 bg-gap-soft text-gap',
    danger: 'border-danger/25 bg-danger-soft text-danger',
  } as const;
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${tones[tone]}`}
    >
      {children}
    </div>
  );
}
